import { createQueryFilters } from 'context/DataContext';
import { CashFlowsValidValues } from 'features/drilldown/cashflows/configurations/cashflow-inputs';
import {
  ChartFragments,
  MonthlyTableFragments,
  PortfolioSummaryMetrics,
  SummaryTableFragments,
} from 'features/drilldown/cashflows/gql/cashflowDataFragments';
import { gql, useQuery } from '@apollo/client';

import { AssetClass, Filter } from '__generated__/globalTypes';
import {
  GetCashflowByPool,
  GetCashflowByPoolVariables,
} from './__generated__/getSelectedPoolCashflow_gql';

// Shared by every consumer of this query's `user.company.pool.performance_summary` selection --
// centralized here, next to the query that produces it, instead of each consumer re-deriving an
// identical alias.
export type PoolCashflowPerfSummary = NonNullable<
  NonNullable<NonNullable<NonNullable<GetCashflowByPool>['user']>['company']>['pool']
>['performance_summary'];

export const POOL_CASHFLOW = gql`
  query GetCashflowByPool(
    $poolId: ID!
    $cdr: Float!
    $cpr: Float!
    $lsr: Float!
    $daysDelay: Int
    $filters: [Filter!]
    $rateType: RateType
    $servicingRate: Float
    $prepaymentAssumptionType: PrepaymentAssumptionType
    $defaultAssumptionType: DefaultAssumptionType
  ) {
    user {
      id
      company {
        id
        pool(id: $poolId) {
          id
          performance_summary(filters: $filters) {
            cashFlows: cash_flows(
              conditionalDefaultRate: $cdr
              conditionalPrepaymentRate: $cpr
              lossSeverityRate: $lsr
              prepaymentAssumptionType: $prepaymentAssumptionType
              defaultAssumptionType: $defaultAssumptionType
              servicingRate: $servicingRate
              daysDelay: $daysDelay
              rateType: $rateType
            ) {
              monthlyCashFlows: monthly_cash_flows {
                ...ChartCashFlowMonth
                ...MonthlyTableCashFlowMonth
              }
              price_yield_matrix {
                spread_matrix
                yield_matrix
              }
              totals {
                ...SummaryTableCashFlowTotals
              }
            }
            ...PortfolioSummaryMetrics
            ...SummaryTablePerformanceSummary
          }
        }
      }
    }
  }
  ${PortfolioSummaryMetrics.performanceSummaryMetrics}
  ${MonthlyTableFragments.cashFlowMonth}
  ${ChartFragments.cashFlowMonth}
  ${SummaryTableFragments.cashFlowTotals}
  ${SummaryTableFragments.performanceSummary}
`;

export const getUseSelectedPoolCashflow = (assetClass: AssetClass | null, filters: Filter[]) => {
  const useSelectedPoolCashflow_gql = (poolId: string, userInputs: CashFlowsValidValues) => {
    const { loading, error, data } = useQuery<GetCashflowByPool, GetCashflowByPoolVariables>(
      POOL_CASHFLOW,
      {
        variables: {
          cdr: userInputs.cdr / 100,
          cpr: userInputs.cpr / 100,
          lsr: userInputs.lsr / 100,
          daysDelay: +userInputs.daysDelay,
          servicingRate: userInputs.servicingRate != null ? userInputs.servicingRate / 100 : null,
          rateType: userInputs.rateType,
          prepaymentAssumptionType: userInputs.prepaymentAssumptionType,
          defaultAssumptionType: userInputs.defaultAssumptionType,
          poolId,
          filters: createQueryFilters(assetClass, filters),
        },
        skip: !poolId,
      },
    );

    return {
      loading,
      error,
      data: data?.user?.company?.pool?.performance_summary,
    };
  };

  return useSelectedPoolCashflow_gql;
};
